import type { IAdViewLink } from "@finn-no/adview-provider-frontend-types";
import type { TrackerData } from "@finn-no/pulse-sdk";
import { Trans, useLingui } from "@lingui/react";
import { EmbeddedPodlet } from "@mobility-item-page/client";
import type { MobilityAd } from "@mobility-item-page/types";
import { ElementType, Intent, TargetType } from "@mobility-item-page/utilities/client";
import { Box } from "@warp-ds/elements/react/box";
import { useAdViewAd } from "src/context/AdViewProviderContext.tsx";
import usePulse from "src/utils/pulse-tracking/usePulse.ts";
import { usePodlets } from "../podlets.ts";

type UsefulLink = {
  url: string;
  text: string;
  trackingObj?: TrackerData;
};

const UsefulResources = () => {
  const ad = useAdViewAd();
  const { trackClick } = usePulse();
  const { pfEconomyLinks } = usePodlets();

  const usefulLinks = useUsefulLinks(ad);
  const moreLinks = "more_info_links" in ad ? ad.more_info_links : [];

  if (usefulLinks.length === 0 && moreLinks.length === 0 && !pfEconomyLinks) {
    return null;
  }

  return (
    <Box bordered className="mt-40 md:mt-16">
      <div className="flex flex-col space-y-16">
        <h2 className="t3 m-0">
          <Trans id="itemPageLayout.usefulResources.title" message="Useful links" />
        </h2>

        {usefulLinks.length > 0 &&
          usefulLinks.map(({ url, text, trackingObj }) => (
            <a
              key={url}
              target="_blank"
              rel="noopener nofollow"
              href={url}
              onClick={() => {
                if (trackingObj) trackClick(trackingObj);
              }}
            >
              {text}
            </a>
          ))}

        <EmbeddedPodlet response={pfEconomyLinks} />

        {moreLinks.length > 0 &&
          moreLinks.map((link: IAdViewLink) => (
            <a
              key={link.uri}
              target="_blank"
              rel="noopener nofollow"
              href={link.uri}
              onClick={() => {
                trackClick(getMoreLinksTrackingObj(link));
              }}
            >
              {link.title}
            </a>
          ))}
      </div>
    </Box>
  );
};

export default UsefulResources;

const useUsefulLinks = (ad: MobilityAd): UsefulLink[] => {
  const { i18n } = useLingui();

  const useFullLinks: UsefulLink[] = [];
  if ("eu_control_link" in ad && ad.eu_control_link) {
    const euControlText = i18n._({
      id: "itemPageLayout.usefulResources.eu_control_link",
      message: "Check the time for the next EU inspection",
      comment: "Link for vehicle information (and EU inspection)",
    });

    // Tracking event: MO_ev51
    const euTracking: TrackerData = {
      name: "Check next EU-control",
      objectId: "MO_ev51",
      elementType: ElementType.Link,
      intent: Intent.Open,
      position: "Useful links",
      target: {
        id: ad.eu_control_link,
        type: TargetType.ExternalContent,
        name: "Go to webpage",
        provider: "Vegvesnet",
      },
    };
    useFullLinks.push({
      url: ad.eu_control_link,
      text: euControlText,
      trackingObj: euTracking,
    });
  }
  if ("liability_link" in ad && ad.liability_link && ad.registration_number) {
    const liabilityText = i18n._({
      id: "itemPageLayout.usefulResources.liability_link",
      message: "Check liens on {registration_number}",
      values: { registration_number: ad.registration_number.toUpperCase() },
      comment: "Link for checking liens on motorcycle",
    });

    // Tracking event: MO_ev52
    const liabilityTracking: TrackerData = {
      name: "Check liens on car",
      objectId: "MO_ev52",
      elementType: ElementType.Link,
      intent: Intent.Open,
      position: "Useful links",
      target: {
        id: ad.liability_link,
        type: TargetType.ExternalContent,
        name: "Go to webpage",
        provider: "Brønnøysundregisteret",
      },
    };
    useFullLinks.push({
      url: ad.liability_link,
      text: liabilityText,
      trackingObj: liabilityTracking,
    });
  }

  if ("small_boat_registry_url" in ad && ad.small_boat_registry_url) {
    const smallBoatText = i18n._({
      id: "itemPageLayout.usefulResources.small_boat_registry_link",
      message: "Check the small boat registry",
      comment: "Link for small boat registry",
    });

    // Tracking event: MO_ev72
    const smallBoatTracking: TrackerData = {
      name: "Check small boat registry",
      objectId: "MO_ev72",
      elementType: ElementType.Link,
      intent: Intent.Open,
      position: "Useful links",
      target: {
        id: ad.small_boat_registry_url,
        type: TargetType.ExternalContent,
        name: "Go to webpage",
        provider: "Småbåtregisteret",
      },
    };

    useFullLinks.push({ url: ad.small_boat_registry_url, text: smallBoatText, trackingObj: smallBoatTracking });
  }

  if ("mobile_home_advice_link" in ad && ad.mobile_home_advice_link) {
    const mobileHomeAdviceText = i18n._({
      id: "itemPageLayout.usefulResources.mobile_home_advice_link",
      message: "Good advice for you who are going to buy a motorhome",
      comment: "Link for advice article for buying mobile home",
    });

    // Tracking event: MO_ev73
    const mobileHomeAdviceTracking: TrackerData = {
      name: "Read mobile-home advice",
      objectId: "MO_ev73",
      elementType: ElementType.Link,
      intent: Intent.Open,
      position: "Useful links",
      target: {
        id: "open-mobile-home-advice-link",
        type: TargetType.Article,
        name: "Mobile home advice",
      },
    };

    useFullLinks.push({
      url: ad.mobile_home_advice_link,
      text: mobileHomeAdviceText,
      trackingObj: mobileHomeAdviceTracking,
    });
  }

  if ("gire_mobility_url" in ad && ad.gire_mobility_url) {
    const gireMobilityText = i18n._({
      id: "itemPageLayout.usefulResources.gire_mobility_link",
      message: "Do you need car transport? Get a quote here",
      comment: "Link for GIRE mobility car transport service",
    });

    // Tracking event: MO_ev76
    const gireMobilityTracking: TrackerData = {
      name: "Gire link",
      objectId: "MO_ev76",
      elementType: ElementType.Link,
      intent: Intent.Open,
      position: "Useful links",
      target: {
        id: "open-gire-mobility-link",
        type: TargetType.ExternalContent,
        name: "Bestill Gire",
        url: ad.gire_mobility_url,
        provider: "Gire Mobility",
      },
    };

    useFullLinks.push({
      url: ad.gire_mobility_url,
      text: gireMobilityText,
      trackingObj: gireMobilityTracking,
    });
  }

  return useFullLinks;
};

const getMoreLinksTrackingObj = (link: IAdViewLink): TrackerData => {
  return {
    name: "More info links",
    objectId: "MO_ev71",
    elementType: ElementType.Link,
    intent: Intent.Open,
    position: "Useful links",
    objectExtras: {
      name: link.title,
    },
    target: {
      id: link.uri,
      type: TargetType.ExternalContent,
      name: "Go to webpage",
    },
  };
};
